/**
 * Sidebar tab content for the Agent feature: hosts `<AgentExplorer>` for
 * the selected feature, wired to MPV's image-ref dispatch and the
 * agent-run-notes / agent-note-click bridges. Extracted from MPV's
 * `renderSidebarViews` so each sidebar tab content lives in its own file.
 */

import * as I from 'immutable';
import React from 'react';

import AgentExplorer from 'app/components/AgentExplorer';
import {
  FewShotExemplarFlow,
  RunExemplarVisibility,
  RunExemplars,
} from 'app/components/AgentExplorer/fewShotExemplarTypes';
import {ApiFeature, ApiNote} from 'app/modules/Remote/Feature';
import * as mapUtils from 'app/utils/mapUtils';

import {MapStateDispatch} from '../MapStateProvider';

interface Props {
  selectedFeature: I.ImmutableOf<ApiFeature>;
  imageRefs: mapUtils.MapImageRefs;
  focusedNoteId: number | string | null;
  mapStateDispatch: MapStateDispatch;
  onActiveRunNotesChange: (notes: ApiNote[], runId: number | null) => void;
  onActiveRunExemplarsChange: (exemplars: RunExemplars | null) => void;
  runExemplarVisibility: RunExemplarVisibility;
  onRunExemplarVisibilityChange: (visibility: RunExemplarVisibility) => void;
  onNoteClick: (note: ApiNote) => void;
  onShowAllObservations: () => void;
  onClearFocus: () => void;
  onUnmount: () => void;
  fewShotFlow: FewShotExemplarFlow;
}

export default function AgentPanel({
  selectedFeature,
  imageRefs,
  focusedNoteId,
  mapStateDispatch,
  onActiveRunNotesChange,
  onActiveRunExemplarsChange,
  runExemplarVisibility,
  onRunExemplarVisibilityChange,
  onNoteClick,
  onShowAllObservations,
  onClearFocus,
  onUnmount,
  fewShotFlow,
}: Props) {
  React.useEffect(() => {
    return () => onUnmount();
  }, [onUnmount]);

  return (
    <AgentExplorer
      featureId={selectedFeature.get('id')}
      imageRefs={imageRefs}
      focusedNoteId={focusedNoteId}
      onActiveRunNotesChange={onActiveRunNotesChange}
      onActiveRunExemplarsChange={onActiveRunExemplarsChange}
      runExemplarVisibility={runExemplarVisibility}
      onRunExemplarVisibilityChange={onRunExemplarVisibilityChange}
      onNoteClick={onNoteClick}
      onShowAllObservations={onShowAllObservations}
      onClearFocus={onClearFocus}
      setImageRefs={(refs: mapUtils.MapImageRefs) =>
        mapStateDispatch({type: 'SET_IMAGE_REFS', imageRefs: refs})
      }
      onEnterCompareMode={() => mapStateDispatch({type: 'SET_MODE', mode: 'COMPARE'})}
      onExitCompareMode={() => mapStateDispatch({type: 'SET_MODE', mode: 'VIEW'})}
      fewShotFlow={fewShotFlow}
    />
  );
}
